import React, { useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'

import { getXCLEARCorrelationInfo } from '@api/utils'
import { Enrollment as EnrollmentClient } from '@clear-denver/tpc-openapi-typescript'
import AuthFlow from '@components/pages/auth/AuthFlow'
import { useEnrollmentContext } from '@contexts/EnrollmentContext'
import { useFocusContext } from '@contexts/FocusTaskContext'
import { yupResolver } from '@hookform/resolvers/yup'
import useAuthenticationApi from '@hooks/useAuthenticationApi'
import { useAuthPipe } from '@hooks/useAuthPipe'
import { useFlag } from '@hooks/useFlag'
import { DiscreteEvent } from '@utils/customEvents'
import { trackFunnelStep } from '@utils/trackFunnelStep'
import { authSchema } from '@validators/schema/enroll/authSchema'
import { useAuthTreatment } from '@hooks/useAuthPipe/lib/useAuthExperiement'

export interface FormData extends Partial<EnrollmentClient.BiographicsLookup> {
  otpSessionId: string
}

export type InitiateAuth = {
  phoneNumber?: string
  email?: string
  otpSessionId: string
}

export enum FormSteps {
  CONTACT = 'Contact',
  VERIFICATION = 'Verification',
}

const AuthPage: React.FC = () => {
  const router = useRouter()
  const { client: authenticationClient } = useAuthenticationApi()
  const { setBiographicAuthInfo, setApptInfo } = useEnrollmentContext()
  const { setFocusTask } = useFocusContext()
  const pipe = useAuthPipe()
  const treatment = useAuthTreatment()
  const attemptsRef = useRef(0)
  const displayAuthenticationRevisedLayout =
    useFlag('layoutRefresh')?.displayAuthenticationRevisedLayout

  const [verificationFormData, setVerificationFormData] =
    useState<FormData | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { register, handleSubmit, formState } = useForm<any>({
    resolver: yupResolver(authSchema),
    mode: 'onChange',
  })

  useEffect(() => {
    setApptInfo(null as never)
  }, [setApptInfo])

  const [step, setStep] = useState<FormSteps>(FormSteps.CONTACT)
  const { errors, isValid } = formState
  const [otpFailureMessage, setOtpFailureMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const desiredApply = router.query?.desiredPath === '/enroll/you'
  const desiredRenew =
    router.query?.desiredPath === '/renewal/eligibility/eligibility'

  useEffect(() => {
    /** EXP: T9_AUTH_STABLE_ROUTING */
    if (!router.isReady) {
      return
    }

    const desiredPath = router.query?.desiredPath
    const isRedirectEntry = !!desiredPath
    pipe.view({
      event: DiscreteEvent.AUTH_VIEW,
      startSession: !isRedirectEntry,
    })

    /** EXP: T5_AUTH_EXIT_NO_DESTINATION */
    /** unconditional (no valid control for dead end) */
    if (
      desiredPath !== '/enroll/you' &&
      desiredPath !== '/renewal/eligibility/eligibility'
    ) {
      pipe.fail({
        type: 'NO_DESIRED_PATH',
        reason: 'Auth page loaded without desiredPath',
        meta: {
          desiredPath,
        },
      })

      setFocusTask({
        isFocusing: true,
        header: 'Something went wrong',
        content: (
          <>
            We had trouble determining your next step. Please try again from the
            homepage.
          </>
        ),
        cta: 'Ok',
        onConfirm: () => router.push('/'),
        canDismiss: false,
      })
      return
    }
  }, [pipe, router, setFocusTask])

  useEffect(() => {
    if (desiredApply) {
      trackFunnelStep('apply', 2, 'auth')
    }
    if (desiredRenew) {
      trackFunnelStep('renew', 2, 'auth')
    }
  }, [desiredApply, desiredRenew])

  async function onSubmitContact(formData: InitiateAuth) {
    setIsLoading(true)
    const { phoneNumber, email } = formData
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const formatedPhoneNumber = phoneNumber ? `+1.${phoneNumber}` : null

    pipe.initiate({
      status: 'begin',
      xCLEARCorrelationInfo,
      url: router.route,
    })

    try {
      const response = await authenticationClient.initiateAuth({
        xCLEARCorrelationInfo,
        otpForm: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          phoneNumber: formatedPhoneNumber!,
          emailAddress: email,
        },
      })
      setBiographicAuthInfo({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        phoneNumber: formatedPhoneNumber!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        emailAddress: email!,
      })

      const hasOtpSessionId = !!response?.otpSessionId

      if (hasOtpSessionId) {
        setVerificationFormData({
          ...formData,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          otpSessionId: response.otpSessionId!,
        })
        setStep(FormSteps.VERIFICATION)
        attemptsRef.current = 0
      }

      pipe.initiate({ status: 'success', hasOtpSessionId })

      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      if (error?.status === 401) {
        pipe.fail({
          type: 'SESSION_EXPIRED',
          reason: '401 during auth initiation',
          meta: { status: '401' },
        })
      }
      pipe.initiate({ status: 'failure', error })
    }
  }

  async function onVerificationSubmit(formData) {
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    attemptsRef.current += 1

    pipe.verify({
      status: 'begin',
      xCLEARCorrelationInfo,
      url: router.route,
    })

    try {
      await authenticationClient.otpChallenge({
        xCLEARCorrelationInfo,
        otpCode: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          otpSessionId: verificationFormData!.otpSessionId,
          otpCode: formData.verificationCode,
        },
      })

      const hasDesiredPath = !!router.query?.desiredPath
      pipe.verify({ status: 'success', hasDesiredPath })

      if (hasDesiredPath) {
        const desiredPath = router.query?.desiredPath as string | undefined
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (!verificationFormData!.otpSessionId) {
          pipe.fail({
            type: 'MISSING_OTP_SESSION',
            reason: 'Cannot navigate to desiredPath without otpSessionId',
            meta: { desiredPath },
          })

          /** EXP: T6_AUTH_BLOCK_NO_TOKEN */
          if (treatment) {
            setFocusTask({
              isFocusing: true,
              header: 'Session expired',
              content: (
                <>
                  For your security, we were unable to continue. Please try
                  again.
                </>
              ),
              cta: 'Okay',
              onConfirm: () => router.push('/'),
              canDismiss: false,
            })
            setIsLoading(false)
            return
          }
        }

        router.push(
          {
            pathname: desiredPath,
            query: {
              otpSessionId: verificationFormData?.otpSessionId,
            },
          },
          desiredPath
        )
      } else {
        pipe.fail({
          type: 'NO_DESIRED_PATH',
          reason: 'Verification succeeded but no desiredPath to navigate to',
        })
        router.push('/')
      }

      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setOtpFailureMessage(
        'Invalid verification code provided, please try again.'
      )

      pipe.verify({
        status: 'failure',
        error,
        attempts: attemptsRef.current,
      })

      /** EXP: T7_AUTH_OTP_EXHAUSTED_RESET */
      if (treatment && attemptsRef.current >= 3) {
        pipe.fail({
          type: 'OTP_EXHAUSTED',
          reason: `OTP verification failed ${attemptsRef.current} times`,
          meta: { attempts: String(attemptsRef.current) },
        })

        setFocusTask({
          isFocusing: true,
          header: 'Too many attempts',
          content: (
            <>
              We had trouble verifying your code. Please confirm your contact
              information and try again.
            </>
          ),
          cta: 'Okay',
          onConfirm: () => {
            setStep(FormSteps.CONTACT)
            attemptsRef.current = 0
            setOtpFailureMessage('')
          },
          canDismiss: false,
        })
      }
    }
  }

  useEffect(() => {
    document.documentElement.style.backgroundColor = '#FBFBF9'
    return () => {
      document.documentElement.style.backgroundColor = ''
    }
  }, [])

  return (
    <AuthFlow
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      displayAuthenticationRevisedLayout={displayAuthenticationRevisedLayout!}
      step={step}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      verificationFormData={verificationFormData!}
      onVerificationSubmit={onVerificationSubmit}
      otpFailureMessage={otpFailureMessage}
      setOtpFailureMessage={setOtpFailureMessage}
      isLoading={isLoading}
      handleSubmit={handleSubmit}
      onSubmitContact={onSubmitContact}
      register={register}
      errors={errors}
      isValid={isValid}
    />
  )
}

export default AuthPage
